import React, { useMemo, useState, useContext, useEffect } from 'react';

import CalculatorTabs from './CalculatorTabs';
import RealEstateIRR from '../../../calculators/Irr';
import PinuiBinuiIRR from '../../../calculators/PinuiBinui';
import PaperApartmentIRR from '../../../calculators/PaperApartment';
import RSUOptions from '../../../calculators/RSUOptionsCalculator';
import CustomerNavbar from './CustomerNavbar';
import { LocalizeContext } from 'react-locale-language';
import CustomerStrategies from './strategies/Strategies';
import { makeApiRequests } from '../../../../helpers/api';
import { ENDPOINTS } from '../../../../helpers/constants';
import { PersonCircle } from 'react-bootstrap-icons';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../../../lib/utils';

const NewCustomerCard = ({
  readOnlyUserLevel,
  isExampleUser,
  parentCustomer,
  activeCustomer,
  adminCompTable,
  onCompTableClick,
  onEditClick,
  onUpdateStrategyTimeClick,
  onDeleteClick,
  onArchiveClick,
  onReapplyCompClick,
  onCustomerGeneralInfoClick,
  onCompareStrategyClick,
  onCollapseIconClick,
  isSidebarCollapsed,
  onCustomerUpdate,
  onSelectedStrategyChange,
  onShareClick,
  fromCustomerPortal,
  onLogsClick,
  onUndoClick,
  onRedoClick,
  onViewFilesClick,
  onAddProfileClick,
  assigneUsers,
  selectedUsers,
  showCollapseIcon
}) => {
  const [objectFormLoadedFromUrl, setObjectFormLoadedFromUrl] = useState();

  const { translate, langCode, isRTL } = useContext(LocalizeContext);

  const [showHowToUseApp, setShowHowToUseApp] = useState(false);
  const [activeTab, setActiveTab] = useState();

  useEffect(() => {
    setActiveTab(
      activeCustomer.showRujumBoard
        ? 'rujum'
        : activeCustomer.allowedCalculators?.length
        ? activeCustomer.allowedCalculators[0]
        : ''
    );
  }, [activeCustomer]);

  const IrrCalculatorSidebar = ({
    customer,

    onCustomerUpdate,
    translate,
    adminCompTable,
    readOnlyMode,
    assigneUsers
  }) => {
    return (
      <div title={`${translate('irr_calculator')} (${customer?.name})`} className="w-full overflow-auto shadow-md">
        <RealEstateIRR
          adminCompTable={adminCompTable}
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          readOnlyMode={readOnlyMode}
          assigneUsers={assigneUsers}
        />
      </div>
    );
  };

  const PinuiBinuiIrrCalculatorSidebar = ({
    customer,
    onCustomerUpdate,
    translate,
    adminCompTable,
    readOnlyMode,
    assigneUsers
  }) => (
    <div title={`${translate('pinui_binui')} (${customer?.name})`} className="w-full overflow-auto shadow-md">
      <PinuiBinuiIRR
        adminCompTable={adminCompTable}
        customer={customer}
        onCustomerUpdate={onCustomerUpdate}
        readOnlyMode={readOnlyMode}
        assigneUsers={assigneUsers}
      />
    </div>
  );

  const PaperApartmentCalculatorSidebar = ({
    customer,

    onCustomerUpdate,
    translate,
    adminCompTable,
    readOnlyMode,
    assigneUsers
  }) => (
    <div title={`${translate('paper_apartment')} (${customer?.name})`} className="w-full overflow-auto shadow-md">
      <PaperApartmentIRR
        adminCompTable={adminCompTable}
        customer={customer}
        onCustomerUpdate={onCustomerUpdate}
        readOnlyMode={readOnlyMode}
        assigneUsers={assigneUsers}
      />
    </div>
  );

  const RSUOptionsCalculatorSidebar = ({
    customer,

    onCustomerUpdate,
    translate,
    adminCompTable,
    readOnlyMode,
    assigneUsers
  }) => (
    <div title={`${translate('rsu_options')} (${customer?.name})`} className="w-full shadow-md">
      <RSUOptions
        adminCompTable={adminCompTable}
        customer={customer}
        onCustomerUpdate={onCustomerUpdate}
        readOnlyMode={readOnlyMode}
        assigneUsers={assigneUsers}
      />
    </div>
  );

  const calculators = [
    {
      key: 'irr',
      name: 'irr_calculator',
      Calculator: IrrCalculatorSidebar
    },
    {
      key: 'pinui-binui',
      name: 'pinui_binui',
      Calculator: PinuiBinuiIrrCalculatorSidebar
    },
    {
      key: 'paper',
      name: 'paper_apartment',
      Calculator: PaperApartmentCalculatorSidebar
    },
    {
      key: 'rsu-options',
      name: 'rsu_options',
      Calculator: RSUOptionsCalculatorSidebar
    }
  ];

  const readOnlyMode = useMemo(
    () =>
      Boolean(isExampleUser) ||
      !activeCustomer?.editable ||
      Boolean(parentCustomer?.deletedBy) ||
      Boolean(readOnlyUserLevel),
    [activeCustomer, isExampleUser, parentCustomer?.deletedBy, readOnlyUserLevel]
  );

  const fetchCompleteDetails = async () => {
    onCustomerUpdate({ ...parentCustomer, loadingError: false });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      requestBody: {
        filter: {
          _id: parentCustomer._id
        }
      }
    });

    if (error) {
      onCustomerUpdate({ ...parentCustomer, loadingError: true });
      return;
    }

    const customer = response[0];
    if (customer) {
      onCustomerUpdate(customer);
    }
  };

  useEffect(() => {
    // Deleted customers don't exist on the server; skip fetching
    if (parentCustomer?.deletedBy) return;
    //auto details fetching is done only once for customer.
    //if there is an error, customer clicks on reload button, which calls fetchCompleteDetails
    if (!['strategies', 'loadingError'].some(k => parentCustomer.hasOwnProperty(k))) fetchCompleteDetails();
  }, [parentCustomer]);

  if (!parentCustomer.strategies) {
    return (
      <div className="w-full mt-2 px-8">
        <div
          className={cn(
            'flex items-center justify-center w-[80%] content-center',
            isSidebarCollapsed ? 'w-full' : 'w-[80%]'
          )}
        >
          <div
            className={`flex max-md:flex-col items-center relative p-3.5 gap-8 shadow-sm rounded-lg w-full bg-white ${
              isRTL ? '!pl-4' : ''
            }`}
          >
            {/* Left Arrow OVERLAY */}

            <div
              className={`flex items-center gap-2 ${
                fromCustomerPortal || (readOnlyMode && !parentCustomer?.deletedBy) ? 'mx-0' : 'mx-2 '
              }`}
            >
              {(!readOnlyMode || parentCustomer?.deletedBy) && !fromCustomerPortal && (
                <div
                  className={cn(
                    'md:absolute left-0 top-0 bottom-0 rtl:right-0 rtl:left-auto rtl:rotate-180 flex items-center justify-center bg-primary p-0.5 cursor-pointer',
                    'rounded-full md:rounded-l-lg md:rounded-r-none'
                  )}
                  onClick={onCollapseIconClick}
                >
                  <ArrowLeft
                    className={cn(
                      'w-4 h-4 text-white',
                      isSidebarCollapsed ? 'rotate-180 rtl:rotate-180' : 'rotate-0 rtl:rotate-0'
                    )}
                  />
                </div>
              )}
              <PersonCircle size={22} className="ms-2 align-text-top" />
              <div className="whitespace-nowrap">
                <div className="text-sm font-bold m-0">{parentCustomer?.boardName || parentCustomer?.name}</div>
                <div className="text-xs font-semibold m-0">{parentCustomer?.email}</div>
              </div>
            </div>
          </div>
        </div>
        {parentCustomer?.deletedBy && (
          <div
            className={`alert alert-danger d-flex align-items-center mt-4 py-3 ${
              isSidebarCollapsed ? 'w-full' : 'w-[80%]'
            }`}
            role="alert"
            style={{ backgroundColor: '#FDECEC' }}
          >
            <span className="text-sm">
              This customer was deleted by <strong>{parentCustomer.deletedBy?.name}</strong>.
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-8 max-md:px-2 w-full flex flex-col items-center mt-2 bg-[#F5F5F5] h-full ">
      <CalculatorTabs
        userName={activeCustomer?.boardName || activeCustomer?.name}
        email={activeCustomer?.email}
        isSidebarCollapsed={!isSidebarCollapsed}
        onCollapseIconClick={onCollapseIconClick}
        showRujumBoard={activeCustomer?.showRujumBoard}
        allowedCalculators={activeCustomer?.allowedCalculators}
        onTabChange={setActiveTab}
        activeTab={activeTab}
        calculators={calculators}
        apartmentProfiles={activeCustomer?.apartmentProfiles}
        onAddProfileClick={onAddProfileClick}
        fromCustomerPortal={fromCustomerPortal}
        readOnlyMode={readOnlyMode}
        showCollapseArrow={parentCustomer?.deletedBy}
      />
      <div
        className="w-full mt-4 rounded-t-lg shadow-md flex flex-col gap-4 py-2.5 bg-white"
        style={{ borderTop: '10px solid #DAEFFF' }}
      >
        <CustomerNavbar
          showHowToUseApp={showHowToUseApp}
          setShowHowToUseApp={setShowHowToUseApp}
          onEditClick={onEditClick}
          onCustomerGeneralInfoClick={onCustomerGeneralInfoClick}
          onCompareStrategyClick={onCompareStrategyClick}
          onViewFilesClick={onViewFilesClick}
          onShareClick={onShareClick}
          onUndoClick={onUndoClick}
          onRedoClick={onRedoClick}
          onLogsClick={onLogsClick}
          onArchiveClick={onArchiveClick}
          onDeleteClick={onDeleteClick}
          onCompTableClick={onCompTableClick}
          activeCustomerId={activeCustomer?._id}
          fromCustomerPortal={fromCustomerPortal}
          readOnlyMode={readOnlyMode}
          onUpdateGroupStrategiesClick={onReapplyCompClick}
          parentCustomer={parentCustomer}
          activeTab={activeTab}
        />
      </div>
      {activeCustomer.showRujumBoard && activeCustomer?.strategies && activeTab === 'rujum' && (
        <div className="w-full shadow-md flex flex-col gap-4 py-2.5 bg-white">
          {/* Strategies Section */}
          <CustomerStrategies
            adminCompTable={adminCompTable}
            activeCustomer={activeCustomer}
            onCustomerUpdate={onCustomerUpdate}
            fromCustomerPortal={fromCustomerPortal}
            onSelectedStrategyChange={onSelectedStrategyChange}
            onUpdateStrategyTimeClick={onUpdateStrategyTimeClick}
            readOnlyMode={readOnlyMode}
            readOnlyUserLevel={readOnlyUserLevel}
            objectFormLoadedFromUrl={objectFormLoadedFromUrl}
            setObjectFormLoadedFromUrl={setObjectFormLoadedFromUrl}
          />
        </div>
      )}
      {[
        {
          key: 'irr',
          Calculator: IrrCalculatorSidebar
        },
        {
          key: 'pinui-binui',
          Calculator: PinuiBinuiIrrCalculatorSidebar
        },
        {
          key: 'paper',
          Calculator: PaperApartmentCalculatorSidebar
        },
        {
          key: 'rsu-options',
          Calculator: RSUOptionsCalculatorSidebar
        }
      ]
        .filter(a => activeCustomer['allowedCalculators']?.includes(a.key))
        .map(
          ({ Calculator, key }) =>
            activeTab === key && (
              <Calculator
                key={key}
                translate={translate}
                customer={activeCustomer}
                adminCompTable={adminCompTable}
                onCustomerUpdate={onCustomerUpdate}
                readOnlyMode={readOnlyMode}
                assigneUsers={assigneUsers}
              />
            )
        )}
    </div>
  );
};

export default NewCustomerCard;
